import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from "react-redux";

import SiteWrapper from "../../SiteWrapper";
import {deletePurchaseAction, getPurchasesAction} from "../../store/actions/PurchaseAction";
import ApprovalStatusComponent from "../../components/ApprovalStatusComponent";

import BreadCrumb from "../../components/BreadCrumb";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import AddIcon from '@material-ui/icons/Add';
import {Link} from "react-router-dom";
import EnhancedTable from "../../components/EnhancedTable";
import dayjs from "dayjs";
import useStyles from "../../styles";
import Grid from "@material-ui/core/Grid";
import {KeyboardDatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DayjsUtils from "@date-io/dayjs";
import {utils} from "../../helper/Utils";
import clsx from "clsx";
import {useFormik} from "formik";
import * as Yup from "yup";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MenuActionComponent from "../../components/MenuActionComponent";


const PurchasesPage = () => {

    const classes = useStyles();

    const dispatch = useDispatch();
    const purchases = useSelector(state => state.purchase.purchases);
    const count = useSelector(state => state.purchase.count);

    const fetchIdRef = useRef(0);

    const fetchData = React.useCallback((pager, query) => {

        let search = Object.assign({}, {limit: pager.pageSize, offset: pager.pageIndex * pager.pageSize}, query);

        const fetchId = ++fetchIdRef.current;

        if (fetchId === fetchIdRef.current) {
            dispatch(getPurchasesAction(search));
        }

    }, [dispatch]);


    useEffect(() => {
        fetchData({pageIndex: utils.DEFAULT_PAGE_INDEX, pageSize: utils.DEFAULT_PAGE_SIZE});
    }, [fetchData]);


    const formik = useFormik({

        initialValues: {

            start_date: dayjs().subtract(utils.filterStart, 'day').format("YYYY-MM-DD"),
            end_date: dayjs().format("YYYY-MM-DD"),
            delivery_note: ''
        },

        validationSchema: Yup.object().shape({
            start_date: Yup.date("YYYY-mm-dd").required('Start date is required'),
            end_date: Yup.date("YYYY-mm-dd").required('End date is required'),
        }),

        onSubmit(values, {setSubmitting}) {

            fetchData({
                pageIndex: utils.DEFAULT_PAGE_INDEX,
                pageSize: utils.DEFAULT_PAGE_SIZE
            }, values);

            setSubmitting(false);
        },

        enableReinitialize: true

    });

    const columns = [
        {
            Header: "Purchase Date",
            accessor: d => (dayjs(d.delivery_date).format(utils.dateFormat))
        },
        {
            Header: "Supplier",
            accessor: d => (d.supplier ? d.supplier.name : '-')
        },
        {
            Header: "Invoice",
            accessor: d => (d.delivery_note ?? '-')
        },
        {
            Header: "Driver",
            accessor: d => (d.driver ?? '-')
        },
        {
            Header: "Truck",
            accessor: d => (d.truck ?? '-')
        },
        {
            Header: "Status",
            accessor: d => <ApprovalStatusComponent status={d.status}/>
        },
        {
            Header: 'Action',
            Cell: row => (<MenuActionComponent item={{
                id: row.row.original.id,
                showView: true,
                hideEdit: row.row.original.status !== 'Saved',
                hideDelete: row.row.original.status !== 'Saved'
            }} url={"purchases"} deleteAction={deletePurchaseAction}/>)
        }
    ];

    return (
        <>
            <SiteWrapper>
                <BreadCrumb crumbs={[{
                    'name': 'Purchases',
                    'link': '#'
                }]}/>

                <Grid container className={classes.marginTop}>

                    <form className={classes.form} noValidate>

                        <Grid container spacing={2}>

                            <Grid item xs={12} sm={4}>
                                <MuiPickersUtilsProvider utils={DayjsUtils}>
                                    <KeyboardDatePicker
                                        margin="normal"
                                        id="date-picker-dialog"
                                        label="Start Date"
                                        maxDate={utils.maxDate}
                                        format="MM/DD/YYYY"
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        value={formik.values.start_date}
                                        onChange={val => formik.setFieldValue('start_date', dayjs(val).format("YYYY-MM-DD"))}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>


                            <Grid item xs={12} sm={4}>
                                <MuiPickersUtilsProvider utils={DayjsUtils}>
                                    <KeyboardDatePicker
                                        margin="normal"
                                        id="date-picker-dialog"
                                        label="End Date"
                                        maxDate={utils.maxDate}
                                        format="MM/DD/YYYY"
                                        KeyboardButtonProps={{
                                            'aria-label': 'change date',
                                        }}
                                        value={formik.values.end_date}
                                        onChange={val => formik.setFieldValue('end_date', dayjs(val).format("YYYY-MM-DD"))}
                                    />
                                </MuiPickersUtilsProvider>
                            </Grid>

                            <Grid item xs={12} sm={4}>
                                <TextField
                                    className={classes.formsWithMui}
                                    name="delivery_note"
                                    id="delivery_note"
                                    label="Invoice No"
                                    value={formik.values.delivery_note || ''} onChange={formik.handleChange}
                                    error={formik.touched.delivery_note && Boolean(formik.errors.delivery_note)}
                                    helperText={formik.touched.delivery_note && formik.errors.delivery_note}
                                />
                            </Grid>

                        </Grid>

                    </form>
                    <Grid container justify="center">
                        <Button
                            onClick={formik.handleSubmit}
                            type="button"
                            size="small"
                            variant="contained"
                            color="secondary"
                            className={clsx(classes.primary, classes.formsWithMui)}
                            disabled={formik.isSubmitting}
                        >
                            Filter
                        </Button>
                    </Grid>
                </Grid>

                <Grid container>
                    <Grid container className={classes.marginTop}>
                        <Grid item xs={12} sm={4}>
                            <Box>
                                <Typography className={clsx(classes.medium, classes.pageTitle)}>
                                    Purchases
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid container item xs={12} sm={8} justify="flex-end">
                            <Box>
                                <Button className={classes.actionButton} startIcon={<AddIcon/>}>
                                    <Link to="/purchases/create" className={classes.unstyledLink}>New Purchase</Link>
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Recent Deposits */}
                    <Grid item xs={12} md={12} lg={12}>
                        {purchases && <EnhancedTable
                            columns={columns}
                            data={purchases}
                            fetchData={fetchData}
                            pageCount={count && count}
                        />}
                    </Grid>
                </Grid>
            </SiteWrapper>
        </>
    );
};

export default PurchasesPage;